/**
 * The onboarding wizard's own API client.
 *
 * Beside the page rather than in `lib/api.ts` for the reason `studioApi.ts` gives: that interface
 * is implemented twice (the mock adapter that ships the demo, and `httpApi`), so every method added
 * to it has to be added to both, and several agents are writing in this repository today. What it
 * reuses is the one thing it must not duplicate — the session's CSRF token, which `httpApi` owns.
 *
 * Author: Danish Khan
 */
import { currentCsrf } from '../lib/httpApi.ts';
import { ApiError } from '../lib/apiTypes.ts';

const BASE = '/api/v1/onboarding';

export type StepId = 'import_pack' | 'connect_meta' | 'sync_templates' | 'invite_team' | 'golden_run' | 'go_live';
export type StepState = 'not_run' | 'passed' | 'failed';
export type LadderStatus = 'development' | 'shadow' | 'live' | 'paused';

export interface Gate {
  id: string;
  title: string;
  satisfied: boolean;
  /** False when nothing in this build can make it true — a Phase 3 dependency, said out loud. */
  satisfiable: boolean;
  detail: string;
  remedy: string;
}

export interface Step {
  step: StepId;
  title: string;
  verifies: string;
  state: StepState;
  detail: string;
  findings: string[];
  result: Record<string, unknown>;
  packDigest: string | null;
  /** Null when it has never run. Never an epoch date pretending to be one. */
  ranAt: string | null;
  ranBy: string;
}

export interface Move {
  to: string;
  allowed: boolean;
  /** Why the move is not on the ladder at all — distinct from a gate refusing it. */
  notAllowedWhy: string | null;
  unmet: Gate[];
}

export interface ChecklistEntry {
  item: string;
  value: string;
  confirmedBy: string;
  confirmedAt: string;
}

export interface PackEditRequired {
  file: string;
  field: string;
  from: string;
  to: string;
  why: string;
}

export interface OnboardingState {
  tenant: {
    id: string;
    status: string;
    packStatus: string;
    packDigest: string;
    packDir: string;
    languages: string[];
    businessHours: string;
  };
  steps: Step[];
  gates: Gate[];
  gatesFor: Record<string, string[]>;
  moves: Move[];
  checklist: ChecklistEntry[];
  checklistItems: string[];
  transitions: { id: string; from: string; to: string; reason: string; movedBy: string; movedAt: string }[];
  packEditRequired: PackEditRequired | null;
}

/**
 * What a status move did, including whether the RUNNING process obeys it yet.
 *
 * The status decides which number the outbox dials — `sendOutboxRow` reads the loaded pack, not
 * the column — so a promotion to `live` the process has not picked up is an agent still answering
 * the business's customers from the staging number while the screen says live, and the retreat is
 * worse: `live → paused` that only moved a row is a brake that did not brake. The API has been
 * returning this pair since the swap was written; it was the console that dropped it on the floor.
 */
export interface MoveResult {
  from: string;
  to: string;
  gates: Gate[];
  packEditRequired: PackEditRequired | null;
  /** True when the next turn obeys this move. False means restart h1-core, and means it. */
  inForce: boolean;
  /** One sentence for the screen, whichever way `inForce` went. */
  inForceDetail: string;
}

export interface OnboardingClient {
  state(): Promise<OnboardingState>;
  runStep(step: StepId, body?: { dir?: string }): Promise<{ step: Step; gates: Gate[] }>;
  confirm(item: string, value: string): Promise<{ checklist: ChecklistEntry[]; gates: Gate[] }>;
  move(to: LadderStatus, reason: string): Promise<MoveResult>;
}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const method = init?.method ?? 'GET';
  const headers = new Headers(init?.headers);
  if (method !== 'GET') {
    if (init?.body !== undefined) headers.set('content-type', 'application/json');
    const csrf = currentCsrf();
    if (csrf) headers.set('x-csrf', csrf);
  }
  const res = await fetch(`${BASE}${path}`, { ...init, headers, credentials: 'same-origin' });
  const text = await res.text();
  let body: unknown = null;
  try { body = text ? JSON.parse(text) : null; } catch { /* handled below */ }
  if (!res.ok) {
    const detail = (body ?? {}) as { error?: { code?: string; message?: string; issues?: { path: string; message: string }[] } };
    throw new ApiError(detail.error?.message ?? `${method} ${path} failed`, res.status, {
      ...(detail.error?.code ? { code: detail.error.code } : {}),
      ...(detail.error?.issues ? { issues: detail.error.issues } : {}),
    });
  }
  return body as T;
}

const json = (value: unknown): RequestInit => ({ method: 'POST', body: JSON.stringify(value) });

export const onboardingApi: OnboardingClient = {
  state: () => call<OnboardingState>(''),
  runStep: (step, body = {}) => call<{ step: Step; gates: Gate[] }>(`/steps/${step}`, json(body)),
  confirm: (item, value) => call<{ checklist: ChecklistEntry[]; gates: Gate[] }>('/checklist', json({ item, value })),
  move: (to, reason) => call<MoveResult>('/status', json({ to, reason })),
};

/** The tone a step's state gets. One mapping, so the wizard and the gate list agree. */
export function stateTone(state: StepState): 'ok' | 'danger' | 'neutral' {
  return state === 'passed' ? 'ok' : state === 'failed' ? 'danger' : 'neutral';
}

/** What a step's state is CALLED. `not_run` must never read as a pass, here least of all. */
export function stateLabel(state: StepState): string {
  return state === 'passed' ? 'passed' : state === 'failed' ? 'failed' : 'not run';
}
